// src/pages/hooks/useFormWithNavigation.tsx
import { createRef, RefObject, useEffect, useRef, useState } from "react";

type Validator<T> = (field: keyof T, value: any, form: T) => string;

export function useFormWithNavigation<T extends Record<string, any>>(
  initialValues: T,
  validateFn: Validator<T>,
  options?: { autofocus?: boolean }
) {
  const [form, setForm] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Record<keyof T, string>>(
    {} as Record<keyof T, string>
  );

  // ==== REFS DINÁMICOS, PERO RESPETANDO LAS RULES OF HOOKS ====
  const refs = useRef<Record<keyof T, RefObject<HTMLInputElement | null>>>(
    {} as Record<keyof T, RefObject<HTMLInputElement | null>>
  );

  // Inicializar refs una vez
  useEffect(() => {
    (Object.keys(initialValues) as (keyof T)[]).forEach((key) => {
      if (!refs.current[key]) {
        refs.current[key] = createRef<HTMLInputElement>();
      }
    });
  }, [initialValues]);

  // Autofocus solo una vez (o cuando cambie la bandera)
  useEffect(() => {
    if (!options?.autofocus) return;
    const firstKey = Object.keys(refs.current)[0] as keyof T;
    refs.current[firstKey]?.current?.focus();
  }, [options?.autofocus]);

  // ==== VALIDAR 1 CAMPO (usando el form ya actualizado) ====
  const validateField = (field: keyof T, value: any, nextForm: T) => {
    const msg = validateFn(field, value, nextForm);
    setErrors((prev) => ({ ...prev, [field]: msg }));
    return msg === "";
  };

  // ==== VALIDAR TODO EL FORM ====
  const validateAll = () => {
    let ok = true;
    const newErrors = {} as Record<keyof T, string>;

    (Object.keys(form) as (keyof T)[]).forEach((key) => {
      const msg = validateFn(key, form[key], form);
      newErrors[key] = msg;
      if (msg) ok = false;
    });

    setErrors(newErrors);
    return ok;
  };

  // ==== HANDLE CHANGE GENÉRICO CON TRANSFORM ====
  const handleChange =
    (field: keyof T, transform?: (value: any) => any) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let raw = e.target.value;
      const value = transform ? transform(raw) : raw;

      const nextForm = { ...form, [field]: value };
      setForm(nextForm);
      validateField(field, value, nextForm);
    };

  // ==== NAVEGACIÓN CON ENTER (solo avanzar, SIN submit) ====
  const handleEnter =
    (field: keyof T) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter") return;

      e.preventDefault();

      const keys = Object.keys(refs.current) as (keyof T)[];
      const index = keys.indexOf(field);

      if (index < 0 || index >= keys.length - 1) return;

      const next = keys[index + 1];
      refs.current[next]?.current?.focus();
    };

  return {
    form,
    setForm,
    errors,
    refs: refs.current,
    handleChange,
    handleEnter,
    validateAll,
  };
}
